import { useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

interface DataManagementOptions {
  offers?: {
    startDate?: string;
    endDate?: string;
    status?: 'all' | 'draft' | 'sent' | 'accepted' | 'rejected';
  };
  statistics?: boolean;
  logs?: boolean;
  calculations?: boolean;
  cache?: boolean;
  payroll?: boolean;
}

export const useDataManagement = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const cleanOffers = async (options: DataManagementOptions['offers']) => {
    try {
      let query = supabase.from('offers').delete();

      if (options?.startDate) {
        query = query.gte('created_at', options.startDate);
      }
      if (options?.endDate) {
        query = query.lte('created_at', options.endDate);
      }
      if (options?.status && options.status !== 'all') {
        query = query.eq('status', options.status);
      }

      // Dla UUID używamy IS DISTINCT FROM null
      query = query.not('id', 'is', null);

      const { error } = await query;
      if (error && !error.message.includes('does not exist')) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error cleaning offers:', error);
      throw error;
    }
  };

  const cleanStatistics = async () => {
    try {
      // Na razie czyść tylko localStorage ze statystykami
      const statsKeys = Object.keys(localStorage).filter(key => 
        key.includes('stats') || key.includes('statistics') || key.includes('analytics')
      );
      
      statsKeys.forEach(key => localStorage.removeItem(key));
      
      return { success: true };
    } catch (error) {
      console.error('Error cleaning statistics:', error);
      throw error;
    }
  };

  const cleanLogs = async () => {
    try {
      // Czyśczenie logów z localStorage
      const logKeys = Object.keys(localStorage).filter(key => 
        key.includes('log') || key.includes('history') || key.includes('activity')
      );
      
      logKeys.forEach(key => localStorage.removeItem(key));
      
      return { success: true };
    } catch (error) {
      console.error('Error cleaning logs:', error);
      throw error;
    }
  };

  const cleanCalculations = async () => {
    try {
      // Czyśczenie zapisanych kalkulacji z localStorage
      const calcKeys = Object.keys(localStorage).filter(key => 
        key.includes('calc') || key.includes('quote') || key.includes('saved_calculation')
      );
      
      calcKeys.forEach(key => localStorage.removeItem(key));
      
      return { success: true };
    } catch (error) {
      console.error('Error cleaning calculations:', error);
      throw error;
    }
  };

  const cleanPayroll = async () => {
    try {
      // Czyść tabele rozliczeń
      const tables = ['payroll_calculations', 'payroll_records'];
      
      for (const table of tables) {
        const { error } = await supabase
          .from(table)
          .delete()
          .not('id', 'is', null);
          
        if (error && !error.message.includes('does not exist')) {
          console.error(`Error cleaning ${table}:`, error);
        }
      }
      
      // Czyść również localStorage
      const payrollKeys = Object.keys(localStorage).filter(key => 
        key.includes('payroll') || key.includes('salary')
      );
      
      payrollKeys.forEach(key => localStorage.removeItem(key));
      
      return { success: true };
    } catch (error) {
      console.error('Error cleaning payroll:', error);
      throw error;
    }
  };

  const cleanCache = async () => {
    try {
      // Czyśczenie localStorage
      const keysToKeep = [
        'plexisystem_session', 
        'plexisystem_theme',
        'supabase.auth.token',
        'notificationSettings',
        'automationRules'
      ];
      
      const allKeys = Object.keys(localStorage);
      
      allKeys.forEach(key => {
        if (!keysToKeep.some(keepKey => key.includes(keepKey))) {
          localStorage.removeItem(key);
        }
      });

      // Czyśczenie sessionStorage
      sessionStorage.clear();

      // Czyść cache przeglądarki jeśli to możliwe
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }

      return { success: true };
    } catch (error) {
      console.error('Error cleaning cache:', error);
      throw error;
    }
  };

  const performCleanup = async (options: DataManagementOptions) => {
    setLoading(true);
    setProgress(0);
    
    const tasks = [];
    
    if (options.offers) {
      tasks.push({ name: 'offers', fn: () => cleanOffers(options.offers) });
    }
    if (options.statistics) {
      tasks.push({ name: 'statistics', fn: cleanStatistics });
    }
    if (options.logs) {
      tasks.push({ name: 'logs', fn: cleanLogs });
    }
    if (options.calculations) {
      tasks.push({ name: 'calculations', fn: cleanCalculations });
    }
    if (options.cache) {
      tasks.push({ name: 'cache', fn: cleanCache });
    }
    if (options.payroll) {
      tasks.push({ name: 'payroll', fn: cleanPayroll });
    }

    const results = {
      success: true,
      errors: [] as string[],
      cleaned: [] as string[]
    };

    try {
      for (let i = 0; i < tasks.length; i++) {
        try {
          await tasks[i].fn();
          results.cleaned.push(tasks[i].name);
        } catch (error: any) {
          results.errors.push(`${tasks[i].name}: ${error.message}`);
          results.success = false;
        }
        setProgress(((i + 1) / tasks.length) * 100);
      }
      
      const message = results.success 
        ? 'Dane zostały wyczyszczone pomyślnie' 
        : `Częściowe czyszczenie. Błędy: ${results.errors.join(', ')}`;
        
      return { success: results.success, message };
    } catch (error) {
      console.error('Error during cleanup:', error);
      throw error;
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const createBackup = async () => {
    try {
      // Pobierz wszystkie dane
      const [offers, clients, products, employees, calculations] = await Promise.all([
        supabase.from('offers').select('*'),
        supabase.from('clients').select('*'),
        supabase.from('products').select('*'),
        supabase.from('employees').select('*'),
        supabase.from('payroll_calculations').select('*')
      ]);

      const backupData = {
        timestamp: new Date().toISOString(),
        version: '1.0',
        data: {
          offers: offers.data || [],
          clients: clients.data || [],
          products: products.data || [],
          employees: employees.data || [],
          payroll_calculations: calculations.data || [],
          localStorage: { ...localStorage }
        }
      };

      // Zapisz backup jako plik JSON
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `plexisystem_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Error creating backup:', error);
      throw error;
    }
  };

  return {
    loading,
    progress,
    performCleanup,
    createBackup
  };
};
